import { Card, CardContent } from "@/components/ui/card";
import { Building2, CheckCircle2, AlertTriangle } from "lucide-react";

interface OrgRow {
  id: string;
  name: string;
  contactCount: number;
  jobCount: number;
  revenue: number;
  outstanding: number;
  stripeStatus: string;
}

interface OrgHealthTableProps {
  orgs: OrgRow[];
  loading: boolean;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function OrgHealthTable({ orgs, loading }: OrgHealthTableProps) {
  if (loading) {
    return <p className="text-sm text-muted-foreground py-4">Loading orgs…</p>;
  }

  if (orgs.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No organizations found.</p>;
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Organization Health
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left px-4 py-2 font-medium">Organization</th>
                <th className="text-right px-3 py-2 font-medium">Contacts</th>
                <th className="text-right px-3 py-2 font-medium">Jobs</th>
                <th className="text-right px-3 py-2 font-medium">Revenue</th>
                <th className="text-right px-3 py-2 font-medium">Outstanding</th>
                <th className="text-center px-3 py-2 font-medium">Stripe</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {orgs.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium">{org.name}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{org.contactCount}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{org.jobCount}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-green-600 font-medium">
                    {fmt(org.revenue)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums" style={{
                    color: org.outstanding > 0 ? "#d97706" : "#6b7280",
                  }}>
                    {fmt(org.outstanding)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {org.stripeStatus === "verified" ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 inline" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-500 inline" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
